import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Button from "../../shared/ui/Button";
import Container from "../../shared/ui/Container";
import "./ContactPage.scss";

// ✅ схема валидации
const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.email("Invalid email address"),
  subject: z.string().min(3, "Subject is required"),
  message: z.string().min(10, "Message is required"),
});

const ContactPage = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    try {
      const res = await fetch("https://dafi-backend.onrender.com/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        console.log("Message sent successfully");
        reset();
      } else {
        console.error("Error sending message");
      }
    } catch (err) {
      console.error("Server error", err);
    }
  };

  return (
    <div className="contact-page">
      <section className="page-header" aria-labelledby="page-title">
        <Container>
          <h1 id="page-title" className="page-header__title">
            Contact Us
          </h1>
          <p className="page-header__subtitle">
            We help companies improve accessibility, remove usability barriers
            and comply with WCAG. Whether you need a full audit, user testing
            with assistive tech, or quick consulting — we’re here to help.
          </p>
        </Container>
      </section>

      <section className="contact-section" aria-labelledby="contact-form-title">
        <div className="contact-info">
          <h2 id="contact-info-title" className="contact-info__title">
            🤝 Not sure where to start?
          </h2>
          <p className="contact-info__text">
            We're here for you. Fill out the form and we'll get back to you as
            soon as possible.
          </p>
          <p className="contact-info__text">We'd love to hear from you!</p>
        </div>

        <div className="contact-form-wrapper">
          <h2 id="contact-form-title" className="contact-form__title">
            💻 Send Us a Message
          </h2>

          {isSubmitSuccessful && (
            <div className="success-message" role="status" aria-live="polite">
              Thank you for your message! We'll get back to you soon.
            </div>
          )}

          <form
            className="contact-form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Name <span aria-label="required">*</span>
              </label>
              <input
                id="name"
                disabled={isSubmitting}
                type="text"
                {...register("name")}
                className={`form-input ${
                  errors.name ? "form-input--error" : ""
                }`}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <span id="name-error" className="form-error" role="alert">
                  {errors.name.message}
                </span>
              )}
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email <span aria-label="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                disabled={isSubmitting}
                {...register("email")}
                className={`form-input ${
                  errors.email ? "form-input--error" : ""
                }`}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <span id="email-error" className="form-error" role="alert">
                  {errors.email.message}
                </span>
              )}
            </div>

            {/* Subject */}
            <div className="form-group">
              <label htmlFor="subject" className="form-label">
                Subject <span aria-label="required">*</span>
              </label>
              <input
                id="subject"
                type="text"
                disabled={isSubmitting}
                {...register("subject")}
                className={`form-input ${
                  errors.subject ? "form-input--error" : ""
                }`}
                aria-invalid={!!errors.subject}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              />
              {errors.subject && (
                <span id="subject-error" className="form-error" role="alert">
                  {errors.subject.message}
                </span>
              )}
            </div>

            {/* Message */}
            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message <span aria-label="required">*</span>
              </label>
              <textarea
                id="message"
                rows="6"
                disabled={isSubmitting}
                {...register("message")}
                className={`form-input form-textarea ${
                  errors.message ? "form-input--error" : ""
                }`}
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-error" : undefined}
              />
              {errors.message && (
                <span id="message-error" className="form-error" role="alert">
                  {errors.message.message}
                </span>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              ariaLabel={
                isSubmitting ? "Sending message..." : "Submit contact form"
              }
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
